/**
 * Rechnungslegungs-Assistent (§1865 BGB)
 * Kontoauszüge hochladen → Live-Fortschritt (SSE) → Checkliste "Was fehlt"
 * → Anforderungen vorbereiten (Briefentwürfe) + Bericht (docx) herunterladen
 */

import { useState, useRef, useEffect } from 'react';
import { useTabSearchParams } from '@/hooks/useTabRoute';
import { useTabs } from '@/contexts/TabContext';
import {
  Wand2, UploadCloud, FileDown, Send, ListChecks, Loader2,
  CheckCircle2, XCircle, AlertTriangle, FileText, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { FinanceWorkbenchErweiterung } from './FinanceWorkbenchErweiterung';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Checklisten-Typen: Beschriftung + Badge-Farbe (rot = kritisch, gelb = Belege)
const FEHLT_TYPEN = {
  fehlendes_konto: { label: 'Fehlendes Konto', klasse: 'bg-red-100 text-red-700 border-red-200' },
  zeitraum_luecke: { label: 'Lücke im Zeitraum', klasse: 'bg-red-100 text-red-700 border-red-200' },
  saldo_sprung: { label: 'Saldo-Sprung', klasse: 'bg-red-100 text-red-700 border-red-200' },
  belege_fehlen: { label: 'Belege fehlen', klasse: 'bg-amber-100 text-amber-700 border-amber-200' },
};

function heuteISO() {
  return new Date().toISOString().slice(0, 10);
}

export function RlAssistent({ selectedKlient, selectedKlientName }) {
  const jahr = new Date().getFullYear();
  // Deep-Link aus der RL-Haupt-Aufgabe: ?rl_von=…&rl_bis=… setzt den Zeitraum vor
  const [searchParams] = useTabSearchParams();
  const [von, setVon] = useState(searchParams.get('rl_von') || `${jahr}-01-01`);
  const [bis, setBis] = useState(searchParams.get('rl_bis') || heuteISO());

  const [dateien, setDateien] = useState([]);          // ausgewählte Dateien (File[])
  const [uploading, setUploading] = useState(false);
  const [fortschritt, setFortschritt] = useState([]);  // [{datei, status, text}]
  const [uploadErgebnis, setUploadErgebnis] = useState(null); // {buchungen_neu, duplikate}

  const [status, setStatus] = useState(null);          // Antwort von /status
  const [statusLoading, setStatusLoading] = useState(false);
  const [hauptaufgabe, setHauptaufgabe] = useState(null); // RL-Haupt-Aufgabe (Arbeitsgedächtnis)
  const { openTab } = useTabs();

  const [anfordernLoading, setAnfordernLoading] = useState(false);
  const [anforderungErgebnis, setAnforderungErgebnis] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const fileRef = useRef();

  const token = localStorage.getItem('olympus_token');
  const authHeaders = { 'Authorization': `Bearer ${token}` };

  // ── Fortschrittsliste aktualisieren (ein Eintrag je Datei) ──
  const updateFortschritt = (datei, patch) => {
    setFortschritt(prev => {
      const idx = prev.findIndex(e => e.datei === datei);
      if (idx === -1) return [...prev, { datei, ...patch }];
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  // ── Status + Checkliste laden ──
  const loadStatus = async (auto = false) => {
    if (!selectedKlient) { if (!auto) toast.error('Bitte einen Betreuten auswählen'); return null; }
    setStatusLoading(true);
    try {
      // Auto-Load (beim Öffnen der Ansicht) ist reiner Lese-Modus und legt
      // KEINE Haupt-Aufgabe an — nur bewusste Aktionen tun das.
      const res = await fetch(
        `${BACKEND_URL}/api/buchhaltung/rl-assistent/status/${selectedKlient}?von=${von}&bis=${bis}${auto ? '&aufgabe_anlegen=false' : ''}`,
        { headers: authHeaders }
      );
      if (!res.ok) throw new Error(`Status-Fehler ${res.status}`);
      const data = await res.json();
      setStatus(data);
      setHauptaufgabe(data.hauptaufgabe || null);
      return data;
    } catch (e) {
      if (!auto) toast.error('Status konnte nicht geladen werden');
      return null;
    } finally {
      setStatusLoading(false);
    }
  };

  // Beim Öffnen der Ansicht (auch via Deep-Link aus der Aufgabe) den Stand
  // des Zeitraums automatisch laden — nie wieder mit leerer Ansicht starten.
  useEffect(() => {
    if (selectedKlient) loadStatus(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKlient]);

  // ── SSE-Verarbeitung (gemeinsam für Direkt-Upload + Akten-Import) ──
  const verarbeiteStream = async (response) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        let data;
        try { data = JSON.parse(line.slice(6)); } catch { continue; }

        if (data.type === 'datei_start') {
          updateFortschritt(data.datei, { status: 'laeuft', text: `Wird verarbeitet (${data.format})...` });
        } else if (data.type === 'ocr_fertig') {
          updateFortschritt(data.datei, { status: 'laeuft', text: `Text erkannt: ${data.zeichen} Zeichen (${data.methode})` });
        } else if (data.type === 'datei_fertig') {
          updateFortschritt(data.datei, {
            status: 'fertig',
            text: `${data.buchungen_neu} neue Buchungen, ${data.duplikate} Duplikate${data.konto ? ` — Konto ${data.konto}` : ''}`,
          });
        } else if (data.type === 'datei_fehler') {
          updateFortschritt(data.datei, {
            status: 'fehler',
            text: data.grund === 'nicht_lesbar'
              ? `Nicht lesbar${data.hinweis ? ` — ${data.hinweis}` : ''}`
              : `Fehler: ${data.grund}${data.hinweis ? ` — ${data.hinweis}` : ''}`,
          });
        } else if (data.type === 'fertig') {
          setUploadErgebnis({ buchungen_neu: data.buchungen_neu, duplikate: data.duplikate });
        }
      }
    }
  };

  // ── Upload + SSE-Verarbeitung ──
  const startUpload = async () => {
    if (!selectedKlient) { toast.error('Bitte einen Betreuten auswählen'); return; }
    if (dateien.length === 0) { toast.error('Bitte mindestens einen Kontoauszug auswählen'); return; }

    setUploading(true);
    setFortschritt([]);
    setUploadErgebnis(null);
    setAnforderungErgebnis(null);

    const formData = new FormData();
    dateien.forEach(f => formData.append('dateien', f));

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/buchhaltung/rl-assistent/upload/${selectedKlient}?von=${von}&bis=${bis}`,
        { method: 'POST', headers: authHeaders, body: formData }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Server-Fehler ${response.status}`);
      }

      await verarbeiteStream(response);

      toast.success('Verarbeitung abgeschlossen');
      setDateien([]);
      if (fileRef.current) fileRef.current.value = '';
      // Nach Abschluss automatisch Checkliste aktualisieren
      await loadStatus();
    } catch (e) {
      toast.error('Upload fehlgeschlagen: ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  // ── Kontoauszüge aus der AKTE übernehmen (Scan-/Mail-Eingang) ──
  const [aktenKandidaten, setAktenKandidaten] = useState(null);
  const [aktenAusgewaehlt, setAktenAusgewaehlt] = useState(new Set());
  const [aktenLoading, setAktenLoading] = useState(false);
  const [aktenImportLoading, setAktenImportLoading] = useState(false);

  const aktenDurchsuchen = async () => {
    if (!selectedKlient) { toast.error('Bitte einen Betreuten auswählen'); return; }
    setAktenLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/buchhaltung/rl-assistent/akten-auszuege/${selectedKlient}`,
        { headers: authHeaders });
      const data = await res.json();
      const kandidaten = data.kandidaten || [];
      setAktenKandidaten(kandidaten);
      // Neue (noch nicht importierte) Auszüge mit vorhandener Datei vorauswählen
      setAktenAusgewaehlt(new Set(kandidaten
        .filter(k => !k.bereits_importiert && k.datei_vorhanden)
        .map(k => k.id)));
    } catch (e) {
      toast.error('Akte durchsuchen fehlgeschlagen: ' + e.message);
    } finally {
      setAktenLoading(false);
    }
  };

  const aktenImportieren = async () => {
    const ids = [...aktenAusgewaehlt];
    if (ids.length === 0) { toast.error('Keine Auszüge ausgewählt'); return; }
    setAktenImportLoading(true);
    setFortschritt([]);
    setUploadErgebnis(null);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/buchhaltung/rl-assistent/akten-auszuege/${selectedKlient}`,
        { method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ dokument_ids: ids, von, bis }) }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Server-Fehler ${response.status}`);
      }
      await verarbeiteStream(response);
      toast.success('Übernahme abgeschlossen');
      await Promise.all([loadStatus(), aktenDurchsuchen()]);
    } catch (e) {
      toast.error('Übernahme fehlgeschlagen: ' + e.message);
    } finally {
      setAktenImportLoading(false);
    }
  };

  // ── Anforderungen vorbereiten (Briefentwürfe für Schreibstube) ──
  const anforderbarePositionen = (status?.fehlt || []).filter(
    f => f.anforderbar_bei != null || (f.typ === 'belege_fehlen' && f.positionen?.length)
  );

  const anforderungenVorbereiten = async () => {
    if (!selectedKlient || anforderbarePositionen.length === 0) return;
    setAnfordernLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/buchhaltung/rl-assistent/anforderungen/${selectedKlient}`,
        {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ positionen: anforderbarePositionen, von, bis }),
        }
      );
      if (!res.ok) throw new Error(`Server-Fehler ${res.status}`);
      const data = await res.json();
      setAnforderungErgebnis(data);
      toast.success(`${data.briefe?.length || 0} Briefentwürfe erstellt — Entwürfe liegen in der Korrespondenz (Akte des Klienten)`);
    } catch (e) {
      toast.error('Anforderungen konnten nicht vorbereitet werden');
    } finally {
      setAnfordernLoading(false);
    }
  };

  // ── Bericht (docx) herunterladen ──
  const berichtHerunterladen = async () => {
    if (!selectedKlient) { toast.error('Bitte einen Betreuten auswählen'); return; }
    setDownloadLoading(true);
    try {
      // Status ggf. nachladen, um export_url zu erhalten
      let aktStatus = status;
      if (!aktStatus?.export_url) {
        aktStatus = await loadStatus();
        if (!aktStatus?.export_url) throw new Error('Export-URL nicht verfügbar');
      }
      if (!aktStatus.bericht_bereit) {
        toast.warning('Achtung: Der Bericht ist noch unvollständig (siehe Checkliste). Download wird trotzdem erstellt.');
      }
      const url = aktStatus.export_url.startsWith('http')
        ? aktStatus.export_url
        : `${BACKEND_URL}${aktStatus.export_url}`;
      const response = await fetch(url, { headers: authHeaders });
      if (!response.ok) throw new Error('Export fehlgeschlagen');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `Rechnungslegungsbericht_${selectedKlientName?.nachname || 'Export'}_${von}_${bis}.docx`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Bericht heruntergeladen');
    } catch (e) {
      toast.error('Bericht-Download fehlgeschlagen');
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <Card className="border-blue-200" data-testid="rl-assistent">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-blue-600" />
          Rechnungslegungs-Assistent
        </CardTitle>
        <CardDescription>
          Kontoauszüge hochladen, Vollständigkeit prüfen und fehlende Unterlagen anfordern
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 1. Zeitraum */}
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1" htmlFor="rl-assistent-von">Zeitraum von</label>
            <input
              id="rl-assistent-von" type="date" value={von} onChange={e => setVon(e.target.value)}
              className="border border-slate-200 rounded-md px-2 py-1.5 text-sm"
              data-testid="rl-assistent-von"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1" htmlFor="rl-assistent-bis">bis</label>
            <input
              id="rl-assistent-bis" type="date" value={bis} onChange={e => setBis(e.target.value)}
              className="border border-slate-200 rounded-md px-2 py-1.5 text-sm"
              data-testid="rl-assistent-bis"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => loadStatus()} disabled={statusLoading || !selectedKlient} data-testid="rl-assistent-status-btn">
            {statusLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Status prüfen
          </Button>
          {hauptaufgabe && (
            <Button variant="outline" size="sm"
              onClick={() => openTab(`/aufgaben/${hauptaufgabe.id}`, hauptaufgabe.titel)}
              title={`${hauptaufgabe.titel} — hier wird jeder Arbeitsschritt chronologisch dokumentiert`}
              data-testid="rl-assistent-aufgabe-btn">
              <ListChecks className="w-4 h-4 mr-2" />
              Zur Aufgabe
            </Button>
          )}
        </div>

        {/* 2. Upload */}
        <div className="border-2 border-dashed border-slate-200 rounded-xl p-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,.csv,.sta,.mt940,.txt"
              onChange={e => setDateien(Array.from(e.target.files || []))}
              className="text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              data-testid="rl-assistent-datei-input"
            />
            <Button onClick={startUpload} disabled={uploading || dateien.length === 0 || !selectedKlient} data-testid="rl-assistent-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
              {uploading ? 'Verarbeite...' : 'Hochladen & verarbeiten'}
            </Button>
            {dateien.length > 0 && !uploading && (
              <span className="text-xs text-slate-400">{dateien.length} {dateien.length === 1 ? 'Datei' : 'Dateien'} ausgewählt</span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-2">Unterstützt: PDF, CSV, MT940</p>

          {/* Bereits eingescannte Auszüge aus der Akte übernehmen (Scan-/Mail-Eingang) */}
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" size="sm" onClick={aktenDurchsuchen}
                disabled={aktenLoading || !selectedKlient} data-testid="rl-assistent-akte-suchen-btn">
                {aktenLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Akte nach Kontoauszügen durchsuchen
              </Button>
              <span className="text-xs text-slate-400">
                findet Auszüge, die über den Scan-Eingang oder E-Mail in der Akte gelandet sind
              </span>
            </div>

            {aktenKandidaten && aktenKandidaten.length === 0 && (
              <p className="text-xs text-slate-400 mt-2">Keine Kontoauszug-Dokumente in der Akte gefunden.</p>
            )}
            {aktenKandidaten && aktenKandidaten.length > 0 && (
              <div className="mt-2 space-y-1">
                {aktenKandidaten.map(k => (
                  <label key={k.id} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`rl-assistent-akte-${k.id}`}>
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      disabled={!k.datei_vorhanden}
                      checked={aktenAusgewaehlt.has(k.id)}
                      onChange={e => {
                        const next = new Set(aktenAusgewaehlt);
                        if (e.target.checked) next.add(k.id); else next.delete(k.id);
                        setAktenAusgewaehlt(next);
                      }}
                    />
                    <span className={k.bereits_importiert ? 'text-slate-400' : 'text-slate-700'}>
                      {k.titel}
                      <span className="text-xs text-slate-400 ml-2">
                        {k.datum}
                        {k.bereits_importiert && ' · bereits übernommen'}
                        {!k.datei_vorhanden && ' · Datei fehlt'}
                      </span>
                    </span>
                  </label>
                ))}
                <Button size="sm" className="mt-2" onClick={aktenImportieren}
                  disabled={aktenImportLoading || aktenAusgewaehlt.size === 0}
                  data-testid="rl-assistent-akte-import-btn">
                  {aktenImportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
                  {aktenAusgewaehlt.size} {aktenAusgewaehlt.size === 1 ? 'Auszug' : 'Auszüge'} übernehmen
                </Button>
              </div>
            )}
          </div>

          {/* Live-Fortschritt je Datei */}
          {fortschritt.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {fortschritt.map((e) => (
                <div key={e.datei} className="flex items-start gap-2 text-sm" data-testid={`rl-assistent-fortschritt-${e.datei}`}>
                  {e.status === 'fertig' && <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />}
                  {e.status === 'fehler' && <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />}
                  {e.status === 'laeuft' && <Loader2 className="w-4 h-4 text-blue-600 animate-spin mt-0.5 shrink-0" />}
                  <div>
                    <span className="font-medium text-slate-700">{e.datei}</span>
                    <span className={`ml-2 ${e.status === 'fehler' ? 'text-red-600' : 'text-slate-500'}`}>{e.text}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {uploadErgebnis && (
            <p className="mt-2 text-sm text-slate-600">
              Gesamt: <span className="font-medium text-green-700">{uploadErgebnis.buchungen_neu} neue Buchungen</span>, {uploadErgebnis.duplikate} Duplikate übersprungen
            </p>
          )}
        </div>

        {/* 3. Checkliste "Was fehlt" */}
        {status && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-slate-500" /> Vollständigkeits-Checkliste
              </p>
              <p className="text-xs text-slate-400">
                {status.buchungen ?? 0} Buchungen · {status.auszuege ?? 0} Auszüge · {status.bekannte_konten ?? 0} bekannte Konten
              </p>
            </div>

            {status.bericht_bereit && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-800">Bericht bereit</AlertTitle>
                <AlertDescription className="text-green-700">
                  Alle Unterlagen für den Zeitraum liegen vollständig vor. Der Bericht kann erstellt werden.
                </AlertDescription>
              </Alert>
            )}

            {(status.fehlt || []).length === 0 && !status.bericht_bereit && (
              <p className="text-sm text-slate-400">Keine offenen Punkte gefunden.</p>
            )}

            {(status.fehlt || []).length > 0 && (
              <div className="space-y-2">
                {status.fehlt.map((f, i) => {
                  const typ = FEHLT_TYPEN[f.typ] || { label: f.typ, klasse: 'bg-slate-100 text-slate-700 border-slate-200' };
                  return (
                    <div key={i} className="flex items-start gap-2 py-2 border-b border-slate-100 last:border-0" data-testid={`rl-assistent-fehlt-${i}`}>
                      <Badge variant="outline" className={`shrink-0 ${typ.klasse}`}>{typ.label}</Badge>
                      <div className="text-sm">
                        <p className="text-slate-700">{f.aktion}</p>
                        <p className="text-xs text-slate-400">
                          {[
                            f.konto && `Konto: ${f.konto}`,
                            f.iban && `IBAN: ${f.iban}`,
                            f.monat && `Monat: ${f.monat}`,
                            f.anzahl != null && `Anzahl: ${f.anzahl}`,
                            f.anforderbar_bei && `anforderbar bei: ${f.anforderbar_bei}`,
                          ].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 4./5. Aktionen */}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                variant="outline"
                onClick={anforderungenVorbereiten}
                disabled={anfordernLoading || anforderbarePositionen.length === 0}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="rl-assistent-anfordern-btn"
              >
                {anfordernLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Anforderungen vorbereiten
                {anforderbarePositionen.length > 0 && ` (${anforderbarePositionen.length})`}
              </Button>
              <Button onClick={berichtHerunterladen} disabled={downloadLoading} data-testid="rl-assistent-bericht-btn">
                {downloadLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                Bericht (§1865) herunterladen
              </Button>
              {!status.bericht_bereit && (
                <span className="flex items-center gap-1 text-xs text-amber-600">
                  <AlertTriangle className="w-3.5 h-3.5" /> Bericht noch unvollständig
                </span>
              )}
            </div>

            {anforderungErgebnis && (
              <Alert className="border-blue-200 bg-blue-50">
                <FileText className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-800">
                  {anforderungErgebnis.briefe?.length || 0} Briefentwürfe erstellt
                </AlertTitle>
                <AlertDescription className="text-blue-700">
                  Die Entwürfe liegen in der Korrespondenz.
                  {anforderungErgebnis.briefe?.length > 0 && (
                    <span> Empfänger: {anforderungErgebnis.briefe.map(b => b.empfaenger).join(', ')}.</span>
                  )}
                  {anforderungErgebnis.aufgaben?.length > 0 && (
                    <span> {anforderungErgebnis.aufgaben.length} Aufgaben angelegt.</span>
                  )}
                  {anforderungErgebnis.hinweis && <span> {anforderungErgebnis.hinweis}</span>}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Finance Workbench Erweiterung (additiv, hinter Feature Flag —
            rendert nichts wenn FINANCE_WORKBENCH_EXTENSIONS_ENABLED=false) */}
        <FinanceWorkbenchErweiterung selectedKlient={selectedKlient} von={von} bis={bis} />
      </CardContent>
    </Card>
  );
}
